import React, { useState, useEffect } from "react";
import { Modal, Button, Form } from "react-bootstrap";

const KPIModal = ({ show, onClose, onSave, initialData, departments = [], users = [] }) => {
  const [formData, setFormData] = useState({
    id: "",
    kpiCode: "",
    description: "",
    unit: "",
    targetValue: 0,
    achievedValue: 0,
    startDate: "",
    endDate: "",
    departmentId: "",
    userId: "",
  });

  const [errors, setErrors] = useState({});

  // Format date: from yyyy-mm-dd to yyyymmdd
  const formatDate = (dateStr) => dateStr.replace(/-/g, "");

  // Generate KPI Code: userId-YYYYMMDD-YYYYMMDD
  const generateKpiCode = (userId, start, end) => {
    if (!userId || !start || !end) return "";
    return `${userId}-${formatDate(start)}-${formatDate(end)}`;
  };

  useEffect(() => {
    if (show) {
      setErrors({});
      if (initialData) {
        setFormData({
          ...initialData,
          startDate: initialData.startDate ? initialData.startDate.split("T")[0] : "",
          endDate: initialData.endDate ? initialData.endDate.split("T")[0] : "",
        });
      } else {
        const today = new Date().toISOString().split("T")[0];
        setFormData({
          id: "",
          kpiCode: "",
          description: "",
          unit: "",
          targetValue: 0,
          achievedValue: 0,
          startDate: today,
          endDate: today,
          departmentId: "",
          userId: "",
        });
      }
    }
  }, [initialData, show]);

  // Update KPI Code when userId, startDate, or endDate changes
  useEffect(() => {
    const { startDate, endDate, userId } = formData;
    if (startDate && endDate && userId) {
      setFormData((prev) => ({
        ...prev,
        kpiCode: generateKpiCode(userId, startDate, endDate),
      }));
      setErrors((prev) => ({ ...prev, kpiCode: false }));
    }
  }, [formData.startDate, formData.endDate, formData.userId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: false }));
  };

  const handleSave = () => {
    const newErrors = {};

    if (!formData.kpiCode.trim()) newErrors.kpiCode = true;
    if (!formData.unit.trim()) newErrors.unit = true;
    if (!formData.departmentId) newErrors.departmentId = true;
    if (!formData.userId) newErrors.userId = true;
    if (!formData.description || !formData.description.trim()) {
      newErrors.description = true;
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const payload = {
      ...formData,
      id: formData.id || 0,
      targetValue: Number(formData.targetValue),
      achievedValue: Number(formData.achievedValue),
      departmentId: Number(formData.departmentId),
      userId: Number(formData.userId),
    };
    onSave(payload);
  };

  return (
    <Modal show={show} onHide={onClose} size="lg" centered backdrop="static">
      <Modal.Header closeButton className="bg-primary text-white">
        <Modal.Title>
          <i className="fas fa-chart-line me-2"></i>
          {formData.id ? "Update KPI" : "Set New KPI"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <div className="row">
            <Form.Group className="col-md-4 mb-3">
              <Form.Label className="fw-bold">KPI Code</Form.Label>
              <Form.Control
                name="kpiCode"
                value={formData.kpiCode}
                placeholder="Auto generated"
                readOnly
                isInvalid={!!errors.kpiCode}
              />
              <Form.Control.Feedback type="invalid">
                KPI Code is required
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="col-md-4 mb-3">
              <Form.Label className="fw-bold">Unit</Form.Label>
              <Form.Control
                name="unit"
                value={formData.unit}
                onChange={handleChange}
                placeholder="Ex: $, pcs, %"
                isInvalid={!!errors.unit}
              />
              <Form.Control.Feedback type="invalid">
                Unit is required
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="col-md-4 mb-3">
              <Form.Label className="fw-bold">Target Value</Form.Label>
              <Form.Control
                type="number"
                name="targetValue"
                value={formData.targetValue}
                onChange={handleChange}
              />
            </Form.Group>
          </div>

          <Form.Group className="mb-3">
            <Form.Label className="fw-bold">KPI Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              name="description"
              value={formData.description}
              onChange={handleChange}
              isInvalid={!!errors.description}
            />
            <Form.Control.Feedback type="invalid">
              KPI Description cannot be empty
            </Form.Control.Feedback>
          </Form.Group>

          <div className="row">
            <Form.Group className="col-md-6 mb-3">
              <Form.Label className="fw-bold">Start Date</Form.Label>
              <Form.Control
                type="date"
                name="startDate"
                value={formData.startDate}
                onChange={handleChange}
              />
            </Form.Group>
            <Form.Group className="col-md-6 mb-3">
              <Form.Label className="fw-bold">End Date</Form.Label>
              <Form.Control
                type="date"
                name="endDate"
                value={formData.endDate}
                onChange={handleChange}
              />
            </Form.Group>
          </div>

          <div className="row">
            <Form.Group className="col-md-6 mb-3">
              <Form.Label className="fw-bold text-primary">Department</Form.Label>
              <Form.Select
                name="departmentId"
                value={formData.departmentId}
                onChange={handleChange}
                isInvalid={!!errors.departmentId}
              >
                <option value="">-- Select Department --</option>
                {departments.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                Department is required
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="col-md-6 mb-3">
              <Form.Label className="fw-bold text-primary">Assignee</Form.Label>
              <Form.Select
                name="userId"
                value={formData.userId}
                onChange={handleChange}
                isInvalid={!!errors.userId}
              >
                <option value="">-- Select Employee --</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.userName}
                  </option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                Assignee is required
              </Form.Control.Feedback>
            </Form.Group>
          </div>
        </Form>
      </Modal.Body>
      <Modal.Footer className="bg-light">
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave}>
          Save KPI
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default KPIModal;